/**
 * Service d'export pour générer et télécharger les fichiers Excel/CSV pour l'atelier
 * MODIFIÉ : Utilise les nouvelles routes POST qui reçoivent les données du localStorage
 */

import { devisApiService } from './DevisApiService';
import { matiereService } from './MatiereService';
import { FallbackAlerts } from './FallbackNotificationService';
import { apiFetch } from './httpClient';

export class ExportService {
  /**
   * Récupère un devis depuis le localStorage
   */
  private static async getDevisFromLocalStorage(devisId: string): Promise<any> {
    try {
      // Utiliser devisApiService pour récupérer le devis
      const devis = await devisApiService.getDevisById(devisId);
      return devis;
    } catch (error) {
      console.error("❌ Erreur récupération devis:", error);
      throw new Error(`Impossible de récupérer le devis ${devisId}`);
    }
  }

  /**
   * Récupère les matières depuis le localStorage/API
   */
  private static async getMatieresFromLocalStorage(): Promise<any[]> {
    try {
      // Utiliser matiereService pour récupérer les matières
      const matieres = await matiereService.getMatieres();
      return matieres;
    } catch (error) {
      console.error("❌ Erreur récupération matières:", error);
      // Retourner un tableau vide en cas d'erreur
      return [];
    }
  }

  /**
   * Exporte les données d'atelier (Excel et CSV) pour un devis
   */
  static async exporterDonneesAtelier(devisId: string): Promise<{ excel: string; csv: string }> {
    try {
      // Récupérer le devis et les matières depuis le localStorage
      const devis = await this.getDevisFromLocalStorage(devisId);
      const matieres = await this.getMatieresFromLocalStorage();
      
      const result = await apiFetch<any>('/devis/export/atelier', {
        method: 'POST',
        body: JSON.stringify({
          devis: devis,
          matieres: matieres
        })
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de l\'export des données d\'atelier');
      }

      return {
        excel: result.excel,
        csv: result.csv
      };
    } catch (error) {
      console.error("❌ Erreur export données atelier:", error);
      throw error;
    }
  }

  /**
   * Exporte uniquement le fichier Excel pour un devis
   */
  static async exporterExcel(devisId: string): Promise<string> {
    try {
      // Récupérer le devis et les matières depuis le localStorage
      const devis = await this.getDevisFromLocalStorage(devisId);
      const matieres = await this.getMatieresFromLocalStorage();
      
      const result = await apiFetch<any>('/devis/export/excel', {
        method: 'POST',
        body: JSON.stringify({
          devis: devis,
          matieres: matieres
        })
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de l\'export Excel');
      }

      return result.excel;
    } catch (error) {
      console.error("❌ Erreur export Excel:", error);
      
      // 🚨 NOTIFICATION FALLBACK - Export Excel
      FallbackAlerts.export('Excel', 'Export Excel via API backend');
      throw error;
    }
  }

  /**
   * Exporte uniquement le fichier CSV pour un devis
   */
  static async exporterCSV(devisId: string): Promise<string> {
    try {
      // Récupérer le devis et les matières depuis le localStorage
      const devis = await this.getDevisFromLocalStorage(devisId);
      const matieres = await this.getMatieresFromLocalStorage();
      
      const result = await apiFetch<any>('/devis/export/csv', {
        method: 'POST',
        body: JSON.stringify({
          devis: devis,
          matieres: matieres
        })
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de l\'export CSV');
      }

      return result.csv;
    } catch (error) {
      console.error("❌ Erreur export CSV:", error);
      
      // 🚨 NOTIFICATION FALLBACK - Export CSV
      FallbackAlerts.export('CSV', 'Export CSV via API backend');
      throw error;
    }
  }

  /**
   * Télécharge un fichier Excel à partir de données base64
   */
  static telechargerExcel(donnees: string, nomFichier: string): void {
    this.telechargerFichier(
      donnees, 
      nomFichier.endsWith('.xlsx') ? nomFichier : `${nomFichier}.xlsx`,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
  }

  /**
   * Télécharge un fichier CSV à partir de données base64
   */
  static telechargerCSV(donnees: string, nomFichier: string): void {
    this.telechargerFichier(
      donnees, 
      nomFichier.endsWith('.csv') ? nomFichier : `${nomFichier}.csv`,
      'text/csv;charset=utf-8'
    );
  }

  /**
   * Télécharge un fichier à partir de données base64
   */
  static telechargerFichier(donnees: string, nomFichier: string, typeMime: string): void {
    try {
      // Décoder les données base64
      const donneesDecoded = atob(donnees);
      const bytes = new Uint8Array(donneesDecoded.length);
      
      for (let i = 0; i < donneesDecoded.length; i++) {
        bytes[i] = donneesDecoded.charCodeAt(i);
      }
      
      // Créer un blob avec les données
      const blob = new Blob([bytes], { type: typeMime });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomFichier;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("❌ Erreur téléchargement fichier:", error);
      throw error;
    }
  }

  /**
   * Génère le nom de fichier pour l'export
   */
  static genererNomFichier(devisReference: string, type: 'excel' | 'csv'): string {
    const date = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
    const extension = type === 'excel' ? 'xlsx' : 'csv';
    return `Atelier_${devisReference}_${date}.${extension}`;
  }

  /**
   * Génère un PDF de devis selon la spécification des statuts
   */
  static async generateDevisPDF(devisId: string, inclureCgv: boolean = false): Promise<{
    success: boolean;
    pdfUrl?: string;
    error?: string;
    statut?: string;
  }> {
    try {
      const result = await apiFetch<any>(`/devis/${devisId}/generate-pdf`, {
        method: 'POST',
        body: JSON.stringify({ inclureCGV: inclureCgv })
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de la génération PDF');
      }
      
      // Créer un blob à partir des données PDF base64
      const pdfData = atob(result.pdf);
      const bytes = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        bytes[i] = pdfData.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(blob);
      
      return {
        success: true,
        pdfUrl,
        statut: result.statut
      };
      
    } catch (error) {
      console.error("❌ Erreur génération PDF:", error);
      
      // 🚨 NOTIFICATION FALLBACK - Génération PDF
      FallbackAlerts.export('PDF', 'Génération PDF via API backend');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Télécharge un PDF généré
   */
  static telechargerPDF(pdfUrl: string, nomFichier: string): void {
    try {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = nomFichier.endsWith('.pdf') ? nomFichier : `${nomFichier}.pdf`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      
    } catch (error) {
      console.error("❌ Erreur téléchargement PDF:", error);
      throw error;
    }
  }

  /**
   * Génère le nom de fichier pour le PDF de devis
   */
  static genererNomFichierPDF(devisReference: string): string {
    const date = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
    return `Devis_${devisReference}_${date}.pdf`;
  }
}
